import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { logger } from "../utils/logger";

/**
 * Custom hook for working with LaunchDarkly feature flags
 */
export const useFeatureFlag = () => {
  // Get all flags
  const flags = useFlags();

  // Get the LaunchDarkly client
  const ldClient = useLDClient();

  /**
   * Check if a feature flag is enabled
   * @param flagKey - The key of the feature flag
   * @param defaultValue - Default value if flag is not found
   */
  const isFeatureEnabled = (flagKey: string, defaultValue = false): boolean => {
    // If the flag exists, return its value, otherwise return the default value
    return typeof flags[flagKey] === "boolean" ? flags[flagKey] : defaultValue;
  };

  /**
   * Get the variation of a feature flag
   * @param flagKey - The key of the feature flag
   * @param defaultValue - Default value if flag is not found
   */
  const getFeatureVariation = <T>(flagKey: string, defaultValue: T): T => {
    return flags[flagKey] !== undefined ? (flags[flagKey] as T) : defaultValue;
  };

  /**
   * Update the user context for LaunchDarkly
   * @param user - The user object to set
   */
  const updateUserContext = async (user: Record<string, any>) => {
    if (ldClient) {
      try {
        await ldClient.identify(user);
        return true;
      } catch (error) {
        // Only log errors in development
        if (process.env.NODE_ENV === "development") {
          logger.error("Error updating LaunchDarkly user context:", error);
        }
        return false;
      }
    }
    return false;
  };

  return {
    flags,
    isFeatureEnabled,
    getFeatureVariation,
    updateUserContext,
    ldClient,
  };
};
